'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Plus, Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { OwnerListing, OwnerListingStatus } from './mockOwnerData';
import { checkBannedWords, validateListingImage, checkImageRelevance } from '@/lib/contentModeration';

interface Props {
  onClose: () => void;
  onAdd: (listing: OwnerListing) => void;
}

interface AddFormData {
  title: string;
  type: string;
  mode: 'sale' | 'rent';
  price: number;
  area: number;
  bedrooms: string;
  bathrooms: string;
  floor: string;
  city: string;
  district: string;
  furnished: boolean;
  description: string;
}

const PROPERTY_TYPES_AR = ['شقة', 'فيلا', 'مكتب', 'أرض', 'شاليه', 'مستودع'];
const CITIES_AR = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة', 'الفجيرة', 'أم القيوين'];
const MAX_FILE_SIZE_MB = 1;
const MAX_IMAGES = 1;

export default function AddListingModal({ onClose, onAdd }: Props) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [uploadedImage, setUploadedImage] = useState<{ file: File; preview: string } | null>(null);
  const [uploadError, setUploadError] = useState('');

  const { register, handleSubmit, watch, trigger, formState: { errors } } = useForm<AddFormData>({
    defaultValues: { mode: 'rent', furnished: false }
  });

  const watchedType = watch('type');

  const handleNext = async () => {
    const valid = await trigger(['title', 'type', 'mode', 'price', 'area']);
    if (!valid) return;

    // Check banned words in title
    const titleValue = watch('title');
    const bannedInTitle = checkBannedWords(titleValue);
    if (bannedInTitle) {
      toast.error('العنوان يحتوي على محتوى غير مسموح به. يرجى مراجعة النص.');
      return;
    }

    setStep(2);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError('');
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Only allow 1 image
    if (uploadedImage) {
      setUploadError('يُسمح بصورة واحدة فقط لكل إعلان');
      e.target.value = '';
      return;
    }

    const file = files[0];

    // Validate size and type
    const sizeError = validateListingImage(file);
    if (sizeError) {
      setUploadError(sizeError);
      e.target.value = '';
      return;
    }

    // Check image relevance by filename heuristic
    const relevanceError = checkImageRelevance(file);
    if (relevanceError) {
      setUploadError(relevanceError);
      e.target.value = '';
      return;
    }

    setUploadedImage({ file, preview: URL.createObjectURL(file) });
    e.target.value = '';
  };

  const removeImage = () => {
    if (uploadedImage) {
      URL.revokeObjectURL(uploadedImage.preview);
      setUploadedImage(null);
    }
  };

  const onSubmit = async (data: AddFormData) => {
    // Check banned words in description
    const bannedInDesc = checkBannedWords(data.description || '');
    if (bannedInDesc) {
      toast.error('الوصف يحتوي على محتوى غير مسموح به. يرجى مراجعة النص.');
      return;
    }

    // Check banned words in district
    const bannedInDistrict = checkBannedWords(data.district || '');
    if (bannedInDistrict) {
      toast.error('حقل الحي يحتوي على محتوى غير مسموح به.');
      return;
    }

    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);

    const newListing: OwnerListing = {
      id: `own-${Date.now()}`,
      title: data.title,
      type: data.type,
      mode: data.mode,
      price: Number(data.price),
      priceUnit: data.mode === 'sale' ? 'AED' : 'AED/شهر',
      city: data.city,
      district: data.district,
      area: Number(data.area),
      bedrooms: data.bedrooms ? Number(data.bedrooms) : null,
      status: 'draft' as OwnerListingStatus,
      views: 0,
      unlocks: 0,
      listedAt: new Date().toISOString().split('T')[0],
      expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      image: uploadedImage ? uploadedImage.preview : "https://img.rocket.new/generatedImages/rocket_gen_img_17b9ab87e-1774514616339.png",
      imageAlt: 'صورة العقار الجديد'
    };

    onAdd(newListing);
    toast.success('تم إضافة الإعلان كمسودة — راجعه ثم انشره');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100 z-10">
          <div className="flex items-center gap-3">
            <h3 className="font-600 text-gray-900">إضافة إعلان جديد</h3>
            <div className="flex items-center gap-1.5">
              <span className={`w-6 h-6 rounded-full text-xs font-700 flex items-center justify-center ${step === 1 ? 'bg-primary-500 text-white' : 'bg-emerald-500 text-white'}`}>1</span>
              <div className={`w-8 h-0.5 ${step === 2 ? 'bg-primary-500' : 'bg-gray-200'}`} />
              <span className={`w-6 h-6 rounded-full text-xs font-700 flex items-center justify-center ${step === 2 ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5">
          {step === 1 &&
          <div className="space-y-4">
              <p className="text-xs text-gray-400 mb-2">الخطوة 1 من 2 — المعلومات الأساسية</p>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1">عنوان الإعلان</label>
                <p className="text-xs text-gray-400 mb-1">اكتب وصفاً واضحاً يجذب الباحثين</p>
                <input
                type="text"
                placeholder="مثال: شقة 3 غرف مفروشة في دبي مارينا"
                {...register('title', { required: 'العنوان مطلوب', minLength: { value: 15, message: 'العنوان قصير جداً — أضف تفاصيل أكثر' } })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
                {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-2">نوع العقار</label>
                <div className="grid grid-cols-3 gap-2">
                  {PROPERTY_TYPES_AR.map((t) =>
                <label key={`add-type-${t}`} className="cursor-pointer">
                      <input type="radio" value={t} {...register('type', { required: 'اختر نوع العقار' })} className="sr-only" />
                      <div className={`py-2 text-sm font-500 rounded-lg border text-center transition-all ${watchedType === t ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                        {t}
                      </div>
                    </label>
                )}
                </div>
                {errors.type && <p className="text-xs text-red-500 mt-1">{errors.type.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-2">للبيع أم للإيجار؟</label>
                <div className="flex gap-3">
                  {(['sale', 'rent'] as const).map((m) =>
                <label key={`add-mode-${m}`} className="flex-1 cursor-pointer">
                      <input type="radio" value={m} {...register('mode')} className="sr-only" />
                      <div className={`py-2.5 text-sm font-600 rounded-xl border text-center transition-all ${watch('mode') === m ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                        {m === 'sale' ? 'للبيع' : 'للإيجار'}
                      </div>
                    </label>
                )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-600 text-gray-700 mb-1">
                    السعر (AED{watch('mode') === 'rent' ? '/شهر' : ''})
                  </label>
                  <input
                  type="number"
                  placeholder="0"
                  {...register('price', { required: 'السعر مطلوب', min: { value: 1, message: 'أدخل سعراً صحيحاً' } })}
                  className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums ${errors.price ? 'border-red-300' : 'border-gray-200'}`} />
                  {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-600 text-gray-700 mb-1">المساحة (م²)</label>
                  <input
                  type="number"
                  placeholder="0"
                  {...register('area', { required: 'المساحة مطلوبة', min: { value: 1, message: 'أدخل مساحة صحيحة' } })}
                  className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums ${errors.area ? 'border-red-300' : 'border-gray-200'}`} />
                  {errors.area && <p className="text-xs text-red-500 mt-1">{errors.area.message}</p>}
                </div>
              </div>

              <button
              type="button"
              onClick={handleNext}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-600 rounded-xl transition-all active:scale-95">
                التالي — تفاصيل الموقع
              </button>
            </div>
          }

          {step === 2 &&
          <div className="space-y-4">
              <p className="text-xs text-gray-400 mb-2">الخطوة 2 من 2 — الموقع والتفاصيل</p>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1">الإمارة</label>
                <select
                {...register('city', { required: 'اختر الإمارة' })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 ${errors.city ? 'border-red-300' : 'border-gray-200'}`}>
                  <option value="">اختر الإمارة</option>
                  {CITIES_AR.map((c) => <option key={`add-city-${c}`} value={c}>{c}</option>)}
                </select>
                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1">الحي / المنطقة</label>
                <input
                type="text"
                placeholder="مثال: دبي مارينا، نخلة جميرا"
                {...register('district', { required: 'الحي مطلوب' })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right ${errors.district ? 'border-red-300' : 'border-gray-200'}`} />
                {errors.district && <p className="text-xs text-red-500 mt-1">{errors.district.message}</p>}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-600 text-gray-700 mb-1">غرف النوم</label>
                  <input type="number" placeholder="—" {...register('bedrooms')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums" />
                </div>
                <div>
                  <label className="block text-xs font-600 text-gray-700 mb-1">الحمامات</label>
                  <input type="number" placeholder="—" {...register('bathrooms')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums" />
                </div>
                <div>
                  <label className="block text-xs font-600 text-gray-700 mb-1">الدور</label>
                  <input type="number" placeholder="—" {...register('floor')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums" />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...register('furnished')} className="w-4 h-4 rounded border-gray-300 text-primary-500" />
                <span className="text-sm text-gray-700">العقار مفروش</span>
              </label>

              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1">وصف الإعلان</label>
                <p className="text-xs text-gray-400 mb-1">أضف تفاصيل تساعد الباحثين على اتخاذ القرار</p>
                <textarea
                rows={3}
                placeholder="صف العقار بالتفصيل — الموقع، المميزات، حالة التشطيب..."
                {...register('description')}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right resize-none" />
              </div>

              {/* Image Upload — 1 image max, 1MB */}
              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1">
                  صورة العقار
                  <span className="text-xs text-gray-400 font-400 mr-2">(صورة واحدة فقط — حتى {MAX_FILE_SIZE_MB} ميجابايت)</span>
                </label>
                <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-1.5 mb-2 border border-amber-100">
                  ⚠ يُسمح فقط بصورة واحدة تخص العقار (مبنى، شقة، غرفة، مدخل). لا تُقبل صور الأشخاص أو الطعام أو السيارات.
                </p>

                {uploadedImage ? (
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-100 group">
                    <img src={uploadedImage.preview} alt="صورة العقار" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 left-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    >
                      <Trash2 size={13} />
                    </button>
                    <span className="absolute bottom-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">رئيسية</span>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-primary-300 transition-colors cursor-pointer block">
                    <Upload size={24} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 font-500">ارفع صورة العقار</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP — حتى {MAX_FILE_SIZE_MB} ميجابايت — صورة واحدة فقط</p>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="sr-only" />
                  </label>
                )}

                {uploadError && <p className="text-xs text-red-500 mt-1.5">{uploadError}</p>}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-2.5 text-sm font-600 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                  رجوع
                </button>
                <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2.5 text-sm font-600 text-white bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2">
                  {loading ?
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> :
                <>
                      <Plus size={15} />
                      نشر الإعلان
                    </>
                }
                </button>
              </div>
            </div>
          }
        </form>
      </div>
    </div>);

}